import React, {useEffect, useState} from 'react';
import {FilePond} from 'react-filepond';
import 'filepond/dist/filepond.min.css';
import {FilePondFile} from 'filepond';
import {showToast} from 'common';
import {FatOutlineButton} from 'common/component/FatOutlineButton';
import {ReportModal} from '../form-components/ReportModal';
import {uploadRosterFile} from './domain/roster';
import './RosterUploadModal.css';

// Browser MIME types for .xls / .xlsx.
const EXCEL_TYPES = [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

const SUCCESS_MESSAGE = 'Your roster file has been successfully uploaded.';

const WAIT_MESSAGE = 'Uploading your roster - this can take up to 20 seconds. Please keep this window open.';

// The request aborted at the 60s client timeout (see uploadRosterFile) - the server may still
// finish on its own, so steer the user to the history table before they retry and double-submit.
const TIMEOUT_MESSAGE = 'The server is taking longer than expected. The upload may still have completed - '
    + 'please close this window and check the upload history before trying again.';

const NETWORK_MESSAGE = 'Unable to reach the server. Please check your internet connection and try again.';

// AV pre-flight: FilePond streams the file to the external scan service; a clean scan returns 2xx,
// an infected file returns 406. Mirrors the (production) server config in ScannedFileInput.tsx.
const scanServerConfig = (token?: string) => ({
    revert: null,
    process: {
        url: `${process.env.REACT_APP_EXTERNAL_SCAN_VIRUS_SERVICE}/scan`,
        headers: {Authorization: token ? `Bearer ${token}` : ''},
    },
});

const scanErrorLabel = (err: any): string => {
    if (err?.code === 406) {
        return 'File contains a virus';
    }
    if (err?.code === 401) {
        return 'Your session may have timed out. Please re-login and try again.';
    }
    return err?.body || 'Unable to scan the file. Please remove it and try again.';
};

// The upload endpoint replies {success: false, message} on failure (BHPNPP-3022) — show that
// message. A non-JSON body means something answered in Portal's place (WAF challenge pages,
// proxies, gateways) — never render it, fall back to a generic line instead.
const uploadErrorLabel = async (response: Response): Promise<string> => {
    if (response.headers.get('content-type')?.includes('application/json')) {
        try {
            const body = await response.json();
            if (typeof body?.message === 'string' && body.message) {
                return body.message;
            }
        } catch {
            // malformed JSON - fall through to the generic message
        }
        return `Upload failed (code ${response.status}). Please try again.`;
    }
    return `Upload failed (code ${response.status}). The request did not reach Portal - `
        + 'please try again, or contact support if this keeps happening.';
};

type RosterUploadModalProps = {
    isShown: boolean;
    onClose: () => void;
    /** Fired after a successful upload so the page can refresh the history table (FE-3). */
    onUploaded?: () => void;
    getToken: () => Promise<string | undefined>;
};

export const RosterUploadModal = ({isShown, onClose, onUploaded, getToken}: RosterUploadModalProps) => {
    const [authToken, setAuthToken] = useState<string | undefined>(undefined);
    useEffect(() => {
        if (isShown) {
            getToken().then(setAuthToken).catch((err) => console.error('Failed to get auth token', err));
        }
    }, [isShown, getToken]);

    const [files, setFiles] = useState<FilePondFile[]>([]);
    // Scan verdict keyed to the FilePond file id, so swapping/removing a file doesn't leave a stale
    // verdict behind (e.g. removing a flagged file and adding a clean one).
    const [scan, setScan] = useState<{id: string; ok: boolean} | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // Single-file modal: uploadable only once *this* file has a recorded clean scan.
    const currentFile = files.length === 1 ? files[0] : null;
    const cleanFile = currentFile && scan?.id === currentFile.id && scan.ok ? (currentFile.file as File) : null;

    const reset = () => {
        setFiles([]);
        setScan(null);
        setSubmitting(false);
        setErrorMessage(null);
    };

    const close = () => {
        if (submitting) {
            return;
        }
        reset();
        onClose();
    };

    const handleUpload = async () => {
        if (!cleanFile) {
            return;
        }
        setSubmitting(true);
        setErrorMessage(null);
        try {
            const response = await uploadRosterFile(cleanFile, await getToken());
            if (!response.ok) {
                setErrorMessage(await uploadErrorLabel(response));
                setSubmitting(false);
                return;
            }
            onUploaded?.();
            showToast.success(SUCCESS_MESSAGE); // failures stay in the modal; success toasts and closes
            reset();
            onClose();
        } catch (err) {
            console.error('Roster upload request failed', err);
            const timedOut = err instanceof DOMException
                && (err.name === 'TimeoutError' || err.name === 'AbortError');
            setErrorMessage(timedOut ? TIMEOUT_MESSAGE : NETWORK_MESSAGE);
            setSubmitting(false);
        }
    };

    // Drives the file item's visual journey past FilePond's own states (see RosterUploadModal.css):
    // ready (neutral) -> uploading (blue, while Portal + Sparkle process) -> failed (red).
    const pondStateClass = submitting ? 'roster-upload-submitting' : errorMessage ? 'roster-upload-failed' : '';

    return (
        <ReportModal visible={isShown} title="Upload Roster" onClose={close} closeDisabled={submitting}>
            <form autoComplete="off"
                  onSubmit={(e) => {
                      e.preventDefault();
                      void handleUpload();
                  }}>
                <div className="flex flex-col w-full p-4">
                    <div className="flex text-base">
                        <div className="w-2/6 text-right">Upload File</div>
                        <div className={`ml-4 w-3/6 roster-upload-dropzone ${pondStateClass}`}>
                            <FilePond
                                allowMultiple={false}
                                maxFiles={1}
                                maxFileSize="20MB"
                                acceptedFileTypes={EXCEL_TYPES}
                                files={files.map((f) => f.file)}
                                onupdatefiles={(fs) => {
                                    setFiles(fs);
                                    // Swapping/removing the file invalidates a previous server verdict.
                                    setErrorMessage(null);
                                }}
                                onprocessfile={(err, file) => {
                                    setScan({id: file.id, ok: !err});
                                }}
                                server={scanServerConfig(authToken)}
                                labelFileProcessing="Scanning..."
                                labelFileProcessingComplete="Ready to upload"
                                labelTapToUndo="tap to remove"
                                labelFileProcessingError={scanErrorLabel}
                                fileValidateTypeLabelExpectedTypes="Expects .xls or .xlsx"
                                labelIdle='Drag & Drop or <span class="filepond--label-action">Browse</span>'
                            />
                        </div>
                    </div>
                    <div className="w-full">
                        <div className="float-right w-4/6 pl-4 text-red-700 text-xs">
                            * Only Excel files (.xls or .xlsx) are accepted
                        </div>
                    </div>
                    {errorMessage && (
                        <div className="w-full">
                            <div className="float-right w-4/6 pl-4 text-red-700 text-xs">{errorMessage}</div>
                        </div>
                    )}
                    {submitting && (
                        <div className="roster-upload-wait-banner" role="status">
                            <span className="roster-upload-spinner"/>
                            {WAIT_MESSAGE}
                        </div>
                    )}
                </div>

                <div className="w-full flex border-t dark:border-gray-600 p-4 justify-end">
                    <div className="flex w-1/4">
                        <FatOutlineButton color={'red'} text={'Cancel'} onClick={close} disabled={submitting}/>
                        <div style={{width: 10}}/>
                        <FatOutlineButton type="submit" color={'green'} text={'Upload'}
                                          disabled={!cleanFile || submitting}/>
                    </div>
                </div>
            </form>
        </ReportModal>
    );
};
